import { Outlet } from '@tanstack/react-router';
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { useSession } from '../../lib/auth-client';
import { BankSessionAlert } from './BankSessionAlert';
import { BrandMark } from './BrandMark';
import { MobileTabBar } from './MobileTabBar';
import { Sidebar } from './Sidebar';

class ErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('ErrorBoundary caught:', error, info);
  }

  override render() {
    if (this.state.hasError) {
      return (
        <div className="flex justify-center py-20">
          <Card className="w-full max-w-lg text-center">
            <CardHeader>
              <div className="text-4xl">⚠️</div>
              <CardTitle>Något gick fel</CardTitle>
              <p className="text-sm text-muted-foreground">
                {this.state.error?.message ?? 'Ett oväntat fel inträffade.'}
              </p>
            </CardHeader>
            <CardContent className="flex justify-center">
              <Button
                type="button"
                onClick={() =>
                  this.setState({
                    hasError: false,
                    error: null,
                  })
                }
                size="lg"
              >
                Försök igen
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }
    return this.props.children;
  }
}

export function AppLayout() {
  const { data: session } = useSession();
  const isAuth = Boolean(session?.session);

  return (
    <div className="min-h-screen flex overflow-x-hidden">
      {/* Desktop sidebar */}
      {isAuth && <Sidebar />}

      {/* Main content area */}
      <div className={cn('flex-1 flex flex-col min-h-screen min-w-0', isAuth && 'lg:pl-60')}>
        {isAuth && (
          <>
            {/* Mobile top bar */}
            <header className="lg:hidden sticky top-0 z-30 bg-background/90 backdrop-blur-md pt-[env(safe-area-inset-top)]">
              <div className="px-4 h-12 flex items-center">
                <BrandMark />
              </div>
            </header>
            <BankSessionAlert />
          </>
        )}
        <main
          className={cn(
            'flex-1 px-4 pt-2 pb-6 md:p-6 lg:p-10 overflow-x-hidden pl-[max(1rem,env(safe-area-inset-left))] pr-[max(1rem,env(safe-area-inset-right))] md:px-6 lg:px-10',
            // Room for the fixed bottom tab bar on phones/tablets
            isAuth && 'pb-[calc(5.5rem+env(safe-area-inset-bottom))] lg:pb-10',
          )}
        >
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>
      {isAuth && <MobileTabBar />}
    </div>
  );
}
